
import { computed, ref, inject, watch, useRoute, onMounted, unref } from '@nuxtjs/composition-api';
import { storeToRefs } from 'pinia';
import { useChosenVariantDetailsStore } from '~/stores/useChosenVariantDetailsStore';
import useTracking from '~/composable/tracking/useTracking';
import useUtilities from '~/composable/useUtilities';
import { calculateInstallmentAmount } from '~/composable/installmentMath';
import { useVoucherPriceAdjustment } from '~/composable/voucher/useVoucherPriceAdjustment';
import { resolveAdjustedDevicePrice } from '~/composable/voucher/resolveDevicePriceForCart';
export default {
  props: {
    defaultInstallmentPeriod: {
      type: Number,
      default: null
    },
    skuInStock: {
      type: Boolean,
      default: null
    }
  },
  setup(props, { emit }) {
    const flipper = inject('flipper')
    const reveal = ref(false)
    const { track } = useTracking()
    const { formatPrice } = useUtilities()
    const route = useRoute()
    const categorySlug = route.value.params.categoryslug.toLowerCase()
    const chosenVariantDetailsStore = useChosenVariantDetailsStore()
    const {
      chosenChannel,
      productDetails
    } = storeToRefs(chosenVariantDetailsStore)
    const selected = ref(null)
    const userHasChosenInstallment = ref(false)

    const {
      calculateAdjustedPrice,
      getPriceFromCartIfVoucherApplies,
      showDiscountCode,
      formatDisplayPrice
    } = useVoucherPriceAdjustment(productDetails)

    const isKontantInstallment = (installment) => {
      return installment?.months === 0 || installment?.displayText === 'Kontant'
    }

    const adjustedDevicePrice = computed(() => {
      const basePriceValue = chosenChannel.value?.productBundlePrice?.productWithSubscriptionPrice || 0
      const expectedDiscountedPrice = calculateAdjustedPrice(basePriceValue)
      const cartPrice = getPriceFromCartIfVoucherApplies()
      return resolveAdjustedDevicePrice(expectedDiscountedPrice, cartPrice)
    })

    const applyVoucherAdjustmentToOption = (option) => {
      if (!showDiscountCode.value) {
        return option
      }
      const devicePrice = adjustedDevicePrice.value
      if (isKontantInstallment(option)) {
        return {
          ...option,
          amount: devicePrice,
          monthlyAmountWithSubscription: devicePrice
        }
      }
      const installmentAmount = calculateInstallmentAmount(devicePrice, option.months)
      if (installmentAmount === null) {
        return option
      }
      const subscriptionPrice = chosenChannel.value?.productBundlePrice?.bundlePrice || 0
      return {
        ...option,
        amount: installmentAmount,
        monthlyAmountWithSubscription: subscriptionPrice + installmentAmount
      }
    }

    const installmentMatrix = computed(() => {
      const options = chosenChannel.value?.installmentOptions || []
      const installments = options.map(applyVoucherAdjustmentToOption)
      if (!installments.some(isKontantInstallment)) {
        installments.push({
          months: 0,
          amount: adjustedDevicePrice.value,
          displayText: 'Kontant',
          subText: ''
        })
      }
      return installments
    })

    const findInstallmentByDefaultPeriod = (matrix) => {
      const defaultPeriod = unref(props.defaultInstallmentPeriod)
      if (defaultPeriod === null || defaultPeriod === undefined) {
        return null
      }
      return matrix.find(installment => installment.months === defaultPeriod) || null
    }

    const getDefaultInstallmentFromMatrix = () => {
      const matrix = installmentMatrix.value
      if (!matrix?.length) {
        return null
      }
      // PDP "Betaling af mobil" defaults to Kontant (months 0), matching pre-voucher behaviour
      const kontantOption = matrix.find(isKontantInstallment)
      if (kontantOption) {
        return kontantOption
      }
      const byProductDefault = findInstallmentByDefaultPeriod(matrix)
      if (byProductDefault) {
        return byProductDefault
      }
      return matrix[0]
    }

    const getDefaultInstallmentIndex = () => {
      const defaultInstallment = getDefaultInstallmentFromMatrix()
      if (!defaultInstallment) {
        return -1
      }
      return installmentMatrix.value.indexOf(defaultInstallment)
    }

    const selectedInstallment = computed(() => {
      if (selected.value !== null && selected.value >= 0 && installmentMatrix.value[selected.value]) {
        return installmentMatrix.value[selected.value]
      }
      return getDefaultInstallmentFromMatrix()
    })

    const summaryInstallment = computed(() => {
      if (userHasChosenInstallment.value) {
        return selectedInstallment.value
      }
      return getDefaultInstallmentFromMatrix()
    })

    const getLabel = (installment) => {
      if (!installment) { return ''; }
      return installment.months > 0 ? 'Afbetaling i ' + installment.months + ' mdr.' : installment.displayText
    }

    const trackChange = (close = false) => {
      track('changeInstallment', { installment: summaryInstallment, label: getLabel(summaryInstallment.value), productDetails, chosenChannel, categorySlug, close })
    }

    const openFlipper = () => flipper.open(reveal)
    const closeFlipper = (close = false) => {
      flipper.close(reveal)
      trackChange(close)
    }

    const applyDefaultInstallmentSelection = () => {
      const defaultInstallment = getDefaultInstallmentFromMatrix()
      if (!defaultInstallment) {
        return
      }
      const defaultIndex = getDefaultInstallmentIndex()
      if (defaultIndex >= 0) {
        selected.value = defaultIndex
      }
      emit('installmentChosen', defaultInstallment)
    }

    const emitUserChosenInstallment = () => {
      userHasChosenInstallment.value = true
      emit('installmentChosen', selectedInstallment.value)
    }

    watch(installmentMatrix, () => {
      if (!userHasChosenInstallment.value) {
        applyDefaultInstallmentSelection()
      }
    }, { immediate: true })

    watch(() => props.defaultInstallmentPeriod, () => {
      if (!userHasChosenInstallment.value) {
        applyDefaultInstallmentSelection()
      }
    })

    onMounted(() => {
      userHasChosenInstallment.value = false
      applyDefaultInstallmentSelection()
    })

    return {
      formatPrice,
      formatDisplayPrice,
      getLabel,
      openFlipper,
      closeFlipper,
      emitUserChosenInstallment,
      flipper,
      reveal,
      selected,
      selectedInstallment,
      summaryInstallment,
      installmentMatrix,
      chosenChannel,
      productDetails,
      summaryInstallmentMonths: computed(() => summaryInstallment.value?.months),
      summaryInstallmentAmount: computed(() => summaryInstallment.value?.amount),
      isCash: computed(() => chosenChannel.value?.channelKey === 'Cash')
    }
  }
}
